import { ThreeMonthPlanItem } from '../../PersonalisedPlan/service/threeMonthPlanService';

export interface PoseItem {
  name: string;
  image: string;
  description?: string;
}

export interface BodyAreaItem {
  name: string;
  currentHealth: string;
  descriptions: string[];
  poses: PoseItem[];
}

// Maps a body area's display name to the ThreeMonthPlanItem field holding its flexibility level.
export const BODY_AREA_KEY_MAP: Record<string, keyof ThreeMonthPlanItem> = {
  Neck: 'neck',
  Shoulders: 'shoulders',
  'Upper Back & Chest': 'upper_back',
  'Lower Back': 'lower_back',
  'Hips & Hip Flexors': 'hip_flexors',
  Hamstrings: 'hamstrings',
  Quadriceps: 'quadriceps',
  'Ankles & Calves': 'calves_ankles'
};

export const PAIN_COLOR_MAP: Record<string, string> = {
  'Very Tight': '#FF0004',
  Tight: '#FF8184',
  Moderate: '#F3A742',
  Flexible: '#2AD76F',
  'Very Flexible': '#2AD76F'
};

export const bodyArea: BodyAreaItem[] = [
  {
    name: 'Neck',
    currentHealth: '',
    descriptions: [
      'Neck stiffness is very common, especially if you spend long hours at a computer, phone, or desk. Tight neck muscles can make turning your head difficult, limit your range of motion, and even cause tension headaches. Yoga helps by loosening the small muscles around your cervical spine, improving rotation, and reducing tension. By gently stretching and opening the neck and upper back, you can feel more relaxed and move your head more freely.'
    ],
    poses: [
      {
        name: 'Seated Neck Stretch',
        image: 'https://staticdata.myyogateacher.com/3_months_plan/seated_neck_stretch.webp',
        description: 'eases stiffness and gently stretches the sides of your neck'
      },
      {
        name: 'Thread the Needle Pose',
        image: 'https://staticdata.myyogateacher.com/3_months_plan/thread_the_needle_pose.webp',
        description: 'releases tension where the neck meets the upper back'
      },
      {
        name: 'Supported Fish Pose',
        image: 'https://staticdata.myyogateacher.com/3_months_plan/supported_fish_pose.webp',
        description: 'opens the throat and chest to relieve compression in the neck'
      }
    ]
  },
  {
    name: 'Shoulders',
    currentHealth: '',
    descriptions: [
      "Rounded shoulders and tight traps are common from sitting hunched or carrying stress in the upper body. This tightness can make lifting, reaching, or rotating your arms uncomfortable. Yoga stretches the shoulder joints, loosens the muscles around the shoulder blades, and improves overall arm mobility. With more flexible shoulders, you'll notice better posture and less strain on your neck."
    ],
    poses: [
      {
        name: 'Cow Face Pose',
        image: 'https://staticdata.myyogateacher.com/3_months_plan/cow_face_pose.webp',
        description: 'stretches the shoulders deeply, especially the back of the arms.'
      },
      {
        name: 'Eagle Arms',
        image: 'https://staticdata.myyogateacher.com/3_months_plan/eagle_arms.webp',
        description: 'opens up the upper back and shoulder blades.'
      },
      {
        name: 'Puppy Pose',
        image: 'https://staticdata.myyogateacher.com/3_months_plan/puppy_pose.webp',
        description: 'lengthens the chest and shoulders, improving range of motion.'
      }
    ]
  },
  {
    name: 'Upper Back & Chest',
    currentHealth: '',
    descriptions: [
      'Tightness in the upper back and chest can make your posture slouch, reduce your breathing capacity, and limit upper-body movement. Yoga helps by stretching the chest, strengthening the back, and increasing spinal flexibility. Regular practice allows your upper body to move more freely and helps counteract the effects of long periods of sitting.'
    ],
    poses: [
      {
        name: 'Cat-Cow',
        image: 'https://staticdata.myyogateacher.com/3_months_plan/cat_cow.webp',
        description: 'mobilizes the upper spine and eases tension.'
      },
      {
        name: 'Cobra Pose',
        image: 'https://staticdata.myyogateacher.com/3_months_plan/cobra_pose.webp',
        description: 'opens the chest while strengthening the muscles along your spine.'
      },
      {
        name: 'Bridge Pose',
        image: 'https://staticdata.myyogateacher.com/3_months_plan/bridge_pose.webp',
        description:
          'stretches the front body while strengthening the upper back, improving posture.'
      }
    ]
  },
  {
    name: 'Lower Back',
    currentHealth: '',
    descriptions: [
      'A tight lower back is common due to prolonged sitting, weak core muscles, or tight hips. This stiffness can make bending, twisting, or lifting uncomfortable. Yoga stretches and strengthens the supporting muscles, improves spinal flexibility, and relieves tension. A more flexible lower back helps everyday movements feel easier and reduces the risk of discomfort.'
    ],
    poses: [
      {
        name: "Child's Pose",
        image: 'https://staticdata.myyogateacher.com/3_months_plan/child_pose.webp',
        description: 'gently relaxes and decompresses the lower spine.'
      },
      {
        name: 'Supine Twist',
        image: 'https://staticdata.myyogateacher.com/3_months_plan/supine_twist.webp',
        description: 'releases tension in the back and hips.'
      },
      {
        name: 'Sphinx Pose',
        image: 'https://staticdata.myyogateacher.com/3_months_plan/sphinx_pose.webp',
        description: 'strengthens the lower back while promoting length and flexibility.'
      }
    ]
  },
  {
    name: 'Hips & Hip Flexors',
    currentHealth: '',
    descriptions: [
      "Tight hips and shortened hip flexors are often the result of long hours of sitting. This tightness can make walking, squatting, or bending difficult, and can also affect posture. Yoga stretches the hip joints, loosens surrounding muscles, and improves pelvic alignment. With more flexible hips, you'll move with greater comfort and ease."
    ],
    poses: [
      {
        name: 'Low Lunge (Anjaneyasana)',
        image: 'https://staticdata.myyogateacher.com/3_months_plan/low_lunge.webp',
        description: 'stretches the hip flexors and front of the hips.'
      },
      {
        name: 'Pigeon Pose',
        image: 'https://staticdata.myyogateacher.com/3_months_plan/pigeon_pose.webp',
        description: 'deeply opens the hip rotators and glutes.'
      },
      {
        name: 'Butterfly Pose',
        image: 'https://staticdata.myyogateacher.com/3_months_plan/butterfly_pose.webp',
        description: 'improves inner-thigh and groin flexibility.'
      }
    ]
  },
  {
    name: 'Hamstrings',
    currentHealth: '',
    descriptions: [
      'Tight hamstrings can limit forward bending, reduce leg mobility, and pull on the lower back, leading to discomfort. Yoga lengthens the hamstrings, improves flexibility, and supports healthy movement patterns in the legs and lower back.'
    ],
    poses: [
      {
        name: 'Forward Fold (Uttanasana)',
        image: 'https://staticdata.myyogateacher.com/3_months_plan/forward_fold.webp',
        description: 'gently stretches the hamstrings along the back of the legs.'
      },
      {
        name: 'Downward Dog',
        image: 'https://staticdata.myyogateacher.com/3_months_plan/downward_dog.webp',
        description: 'stretches the legs while engaging the arms and shoulders.'
      },
      {
        name: 'Seated Forward Bend',
        image: 'https://staticdata.myyogateacher.com/3_months_plan/seated_forward_bend.webp',
        description: 'deepens the hamstring stretch safely while keeping the spine long.'
      }
    ]
  },
  {
    name: 'Quadriceps',
    currentHealth: '',
    descriptions: [
      'Tight quadriceps can pull on the knees, tilt the pelvis forward, and affect lower-back alignment. Yoga stretches the front of the thighs, balances muscle tension between the front and back of the legs, and supports better movement in daily activities like walking, running, or climbing stairs.'
    ],
    poses: [
      {
        name: 'Standing Quad Stretch',
        image: 'https://staticdata.myyogateacher.com/3_months_plan/standing_quad_stretch.webp',
        description: 'lengthens the front thighs and releases tension.'
      },
      {
        name: 'Low Lunge Quad Stretch',
        image: 'https://staticdata.myyogateacher.com/3_months_plan/low_lunge_quad_stretch.webp',
        description: 'stretches quads while opening the hips.'
      },
      {
        name: 'Bow Pose',
        image: 'https://staticdata.myyogateacher.com/3_months_plan/bow_pose.webp',
        description: 'stretches the entire front body, including quads and hip flexors.'
      }
    ]
  },
  {
    name: 'Ankles & Calves',
    currentHealth: '',
    descriptions: [
      'Tight calves and limited ankle flexibility can affect balance, knee alignment, and walking or running. Yoga stretches the lower legs, improves ankle mobility, and strengthens the muscles that support your movement. With more flexible ankles and calves, everyday activities feel easier, and you reduce the risk of strain or injury.'
    ],
    poses: [
      {
        name: 'Downward Dog (heels down)',
        image: 'https://staticdata.myyogateacher.com/3_months_plan/downward_dog.webp',
        description: 'stretches calves and Achilles tendons.'
      },
      {
        name: 'Garland Pose (Malasana)',
        image: 'https://staticdata.myyogateacher.com/3_months_plan/garland_pose.webp',
        description: 'opens the ankles and improves mobility.'
      },
      {
        name: 'Standing Calf Stretch',
        image: 'https://staticdata.myyogateacher.com/3_months_plan/standing_calf_stretch.webp',
        description: 'lengthens lower-leg muscles for better movement and stability.'
      }
    ]
  }
];

// Female pose images — fill in the empty strings below once images are uploaded
export const femaleBodyArea: BodyAreaItem[] = [
  {
    name: 'Neck',
    currentHealth: '',
    descriptions: [
      'Neck stiffness is very common, especially if you spend long hours at a computer, phone, or desk. Tight neck muscles can make turning your head difficult, limit your range of motion, and even cause tension headaches. Yoga helps by loosening the small muscles around your cervical spine, improving rotation, and reducing tension. By gently stretching and opening the neck and upper back, you can feel more relaxed and move your head more freely.'
    ],
    poses: [
      {
        name: 'Seated Neck Stretch',
        image: 'https://staticdata.myyogateacher.com/3_months_plan/seated_neck_stretch_female.webp',
        description: 'eases stiffness and gently stretches the sides of your neck'
      },
      {
        name: 'Thread the Needle Pose',
        image:
          'https://staticdata.myyogateacher.com/3_months_plan/thread_the_needle_pose_female.webp',
        description: 'releases tension where the neck meets the upper back'
      },
      {
        name: 'Supported Fish Pose',
        image: 'https://staticdata.myyogateacher.com/3_months_plan/supported_fish_pose_female.webp',
        description: 'opens the throat and chest to relieve compression in the neck'
      }
    ]
  },
  {
    name: 'Shoulders',
    currentHealth: '',
    descriptions: [
      "Rounded shoulders and tight traps are common from sitting hunched or carrying stress in the upper body. This tightness can make lifting, reaching, or rotating your arms uncomfortable. Yoga stretches the shoulder joints, loosens the muscles around the shoulder blades, and improves overall arm mobility. With more flexible shoulders, you'll notice better posture and less strain on your neck."
    ],
    poses: [
      {
        name: 'Cow Face Pose',
        image: 'https://staticdata.myyogateacher.com/3_months_plan/cow_face_pose_female.webp',
        description: 'stretches the shoulders deeply, especially the back of the arms.'
      },
      {
        name: 'Eagle Arms',
        image: 'https://staticdata.myyogateacher.com/3_months_plan/eagle_arms_female.webp',
        description: 'opens up the upper back and shoulder blades.'
      },
      {
        name: 'Puppy Pose',
        image: 'https://staticdata.myyogateacher.com/3_months_plan/puppy_pose_female.webp',
        description: 'lengthens the chest and shoulders, improving range of motion.'
      }
    ]
  },
  {
    name: 'Upper Back & Chest',
    currentHealth: '',
    descriptions: [
      'Tightness in the upper back and chest can make your posture slouch, reduce your breathing capacity, and limit upper-body movement. Yoga helps by stretching the chest, strengthening the back, and increasing spinal flexibility. Regular practice allows your upper body to move more freely and helps counteract the effects of long periods of sitting.'
    ],
    poses: [
      {
        name: 'Cat-Cow',
        image: 'https://staticdata.myyogateacher.com/3_months_plan/cat_cow_female.webp',
        description: 'mobilizes the upper spine and eases tension.'
      },
      {
        name: 'Cobra Pose',
        image: 'https://staticdata.myyogateacher.com/3_months_plan/cobra_pose_female.webp',
        description: 'opens the chest while strengthening the muscles along your spine.'
      },
      {
        name: 'Bridge Pose',
        image: 'https://staticdata.myyogateacher.com/3_months_plan/bridge_pose_female.webp',
        description:
          'stretches the front body while strengthening the upper back, improving posture.'
      }
    ]
  },
  {
    name: 'Lower Back',
    currentHealth: '',
    descriptions: [
      'A tight lower back is common due to prolonged sitting, weak core muscles, or tight hips. This stiffness can make bending, twisting, or lifting uncomfortable. Yoga stretches and strengthens the supporting muscles, improves spinal flexibility, and relieves tension. A more flexible lower back helps everyday movements feel easier and reduces the risk of discomfort.'
    ],
    poses: [
      {
        name: "Child's Pose",
        image: 'https://staticdata.myyogateacher.com/3_months_plan/child_pose_female.webp',
        description: 'gently relaxes and decompresses the lower spine.'
      },
      {
        name: 'Supine Twist',
        image: 'https://staticdata.myyogateacher.com/3_months_plan/supine_twist_female.webp',
        description: 'releases tension in the back and hips.'
      },
      {
        name: 'Sphinx Pose',
        image: 'https://staticdata.myyogateacher.com/3_months_plan/sphinx_pose_female.webp',
        description: 'strengthens the lower back while promoting length and flexibility.'
      }
    ]
  },
  {
    name: 'Hips & Hip Flexors',
    currentHealth: '',
    descriptions: [
      "Tight hips and shortened hip flexors are often the result of long hours of sitting. This tightness can make walking, squatting, or bending difficult, and can also affect posture. Yoga stretches the hip joints, loosens surrounding muscles, and improves pelvic alignment. With more flexible hips, you'll move with greater comfort and ease."
    ],
    poses: [
      {
        name: 'Low Lunge (Anjaneyasana)',
        image: 'https://staticdata.myyogateacher.com/3_months_plan/low_lunge_female.webp',
        description: 'stretches the hip flexors and front of the hips.'
      },
      {
        name: 'Pigeon Pose',
        image: 'https://staticdata.myyogateacher.com/3_months_plan/pigeon_pose_female.webp',
        description: 'deeply opens the hip rotators and glutes.'
      },
      {
        name: 'Butterfly Pose',
        image: 'https://staticdata.myyogateacher.com/3_months_plan/butterfly_pose_female.webp',
        description: 'improves inner-thigh and groin flexibility.'
      }
    ]
  },
  {
    name: 'Hamstrings',
    currentHealth: '',
    descriptions: [
      'Tight hamstrings can limit forward bending, reduce leg mobility, and pull on the lower back, leading to discomfort. Yoga lengthens the hamstrings, improves flexibility, and supports healthy movement patterns in the legs and lower back.'
    ],
    poses: [
      {
        name: 'Forward Fold (Uttanasana)',
        image: 'https://staticdata.myyogateacher.com/3_months_plan/forward_fold_female.webp',
        description: 'gently stretches the hamstrings along the back of the legs.'
      },
      {
        name: 'Downward Dog',
        image: 'https://staticdata.myyogateacher.com/3_months_plan/downward_dog_female.webp',
        description: 'stretches the legs while engaging the arms and shoulders.'
      },
      {
        name: 'Seated Forward Bend',
        image: 'https://staticdata.myyogateacher.com/3_months_plan/seated_forward_bend_female.webp',
        description: 'deepens the hamstring stretch safely while keeping the spine long.'
      }
    ]
  },
  {
    name: 'Quadriceps',
    currentHealth: '',
    descriptions: [
      'Tight quadriceps can pull on the knees, tilt the pelvis forward, and affect lower-back alignment. Yoga stretches the front of the thighs, balances muscle tension between the front and back of the legs, and supports better movement in daily activities like walking, running, or climbing stairs.'
    ],
    poses: [
      {
        name: 'Standing Quad Stretch',
        image:
          'https://staticdata.myyogateacher.com/3_months_plan/standing_quad_stretch_female.webp',
        description: 'lengthens the front thighs and releases tension.'
      },
      {
        name: 'Low Lunge Quad Stretch',
        image:
          'https://staticdata.myyogateacher.com/3_months_plan/low_lunge_quad_stretch_female.webp',
        description: 'stretches quads while opening the hips.'
      },
      {
        name: 'Bow Pose',
        image: 'https://staticdata.myyogateacher.com/3_months_plan/bow_pose_female.webp',
        description: 'stretches the entire front body, including quads and hip flexors.'
      }
    ]
  },
  {
    name: 'Ankles & Calves',
    currentHealth: '',
    descriptions: [
      'Tight calves and limited ankle flexibility can affect balance, knee alignment, and walking or running. Yoga stretches the lower legs, improves ankle mobility, and strengthens the muscles that support your movement. With more flexible ankles and calves, everyday activities feel easier, and you reduce the risk of strain or injury.'
    ],
    poses: [
      {
        name: 'Downward Dog (heels down)',
        image: 'https://staticdata.myyogateacher.com/3_months_plan/downward_dog_female.webp',
        description: 'stretches calves and Achilles tendons.'
      },
      {
        name: 'Garland Pose (Malasana)',
        image: 'https://staticdata.myyogateacher.com/3_months_plan/garland_pose_female.webp',
        description: 'opens the ankles and improves mobility.'
      },
      {
        name: 'Standing Calf Stretch',
        image:
          'https://staticdata.myyogateacher.com/3_months_plan/standing_calf_stretch_female.webp',
        description: 'lengthens lower-leg muscles for better movement and stability.'
      }
    ]
  }
];
